import React, { useEffect, useState } from 'react';
import { Box } from '@mui/material';

import { apiRequest } from '@/src/services';
import { getValidSelectValue, getZeroOption, type Option, Constants } from '@/src/utils';
import {
    type StandardsReportFiltersProps,
    type InitialOptions,
    type SchoolClassOptions,
    type ExamOption,
    type ClassOption,
} from '../models/filterDTOs';
import {
    ErrorMessage,
    FilterIcon,
    ICON_COLORS,
    SimpleButton,
    SimpleSingleSelect,
    FilterBarContainer,
} from '@/src/ui';

const AllOption = getZeroOption('All');
const LoadingOption = getZeroOption('Loading...');
const SelectDistrictOption = getZeroOption('Select District');
const SelectSchoolOption = getZeroOption('Select School');
const SelectGradeOption = getZeroOption('Select Grade');
const SelectClassOption = getZeroOption('Select Class');
const SelectExamOption = getZeroOption('Select Exam');
const NoClassOption = getZeroOption('No Classes Available');
const NoExamOption = getZeroOption('No Exams Available');
const AllStudentsOption = getZeroOption('All');

const requiredFields = [
    'districtId',
    'schoolId',
    'gradeLevelMasterId',
    'classId',
    'examId',
] as const;
function getMissingFields(fs: any): string[] {
    return requiredFields
        .filter(
            (key) =>
                !fs[key] ||
                Number(fs[key]) === Number(SelectDistrictOption.id) ||
                Number(fs[key]) === Number(SelectSchoolOption.id) ||
                Number(fs[key]) === Number(SelectGradeOption.id) ||
                Number(fs[key]) === Number(SelectClassOption.id) ||
                Number(fs[key]) === Number(SelectExamOption.id)
        )
        .map((key) => Constants.fieldLabels[key] || key);
}

const getLoadingProps = (opts: Option[], val: number, loading: boolean) =>
    loading
        ? { options: [LoadingOption], value: 0, disabled: true }
        : { options: opts, value: val };

const StandardsReportFilters: React.FC<StandardsReportFiltersProps> = ({
    filterState,
    setFilterState,
    refreshTableData,
    onExamNameChange,
}) => {
    const [error, setError] = useState('');
    const [fieldsChanged, setFieldsChanged] = useState(false);

    const [districtOptions, setDistrictOptions] = useState<Option[]>([LoadingOption]);
    const [schoolOptions, setSchoolOptions] = useState<Option[]>([SelectSchoolOption]);
    const [gradeLevelOptions, setGradeLevelOptions] = useState<Option[]>([LoadingOption]);
    const [classOptions, setClassOptions] = useState<Option[]>([SelectClassOption]);
    const [examTypeOptions, setExamTypeOptions] = useState<Option[]>([AllOption]);
    const [studentOptions, setStudentOptions] = useState<Option[]>([AllStudentsOption]);

    const [classData, setClassData] = useState<ClassOption[]>([]);
    const [filtersLoading, setFiltersLoading] = useState(true);
    const [initialLoadComplete, setInitialLoadComplete] = useState(false);

    const [allExamOptions, setAllExamOptions] = useState<ExamOption[] | null>(null);
    const [examOptions, setExamOptions] = useState<Option[]>([SelectExamOption]);
    const [examLoading, setExamLoading] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const res = await apiRequest<InitialOptions>(
                    'GET',
                    'utility/filterData?getGradeLevelMaster=true&getDistrict=true&getExamTypeMaster=true'
                );
                const data = res.data;

                setDistrictOptions([
                    SelectDistrictOption,
                    ...(data.districtOptions ?? []).sort((a, b) => a.name.localeCompare(b.name)),
                ]);
                setGradeLevelOptions([SelectGradeOption, ...(data.gradeLevelMasterOptions ?? [])]);
                setExamTypeOptions([
                    AllOption,
                    ...data.examTypeMasterOptions.sort((a, b) => a.name.localeCompare(b.name)),
                ]);
            } catch {
                setError(
                    'Failed to fetch initial filter options. Please refresh the page or try again.'
                );
            } finally {
                setInitialLoadComplete(true);
                if (filterState.classId) {
                    fetchStudentOptions(filterState.classId);
                }
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [setFilterState]);

    useEffect(() => {
        if (
            initialLoadComplete &&
            filterState &&
            filterState.districtId &&
            filterState.districtId !== Number(SelectDistrictOption.id)
        ) {
            fetchSchoolClassOptions(filterState.districtId).then(() => {
                setFiltersLoading(false);
            });
        } else if (initialLoadComplete) {
            setFiltersLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialLoadComplete, filterState.districtId]);

    useEffect(() => {
        if (
            filterState &&
            filterState.schoolId &&
            filterState.schoolId !== Number(SelectSchoolOption.id) &&
            filterState.gradeLevelMasterId &&
            filterState.gradeLevelMasterId !== Number(SelectGradeOption.id) &&
            classData.length > 0
        ) {
            updateClassOptions(filterState.gradeLevelMasterId, filterState.schoolId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [classData, filterState.schoolId, filterState.gradeLevelMasterId]);

    const fetchSchoolClassOptions = async (districtId: number) => {
        if (!districtId || districtId === Number(SelectDistrictOption.id)) {
            setSchoolOptions([SelectSchoolOption]);
            setClassOptions([SelectClassOption]);
            setFilterState((prev) => ({
                ...prev,
                schoolId: Number(SelectSchoolOption.id),
                gradeLevelMasterId: Number(SelectGradeOption.id),
                classId: Number(SelectClassOption.id),
            }));
            return;
        }

        try {
            const res = await apiRequest<SchoolClassOptions>(
                'GET',
                `utility/filterData?getSchool=true&getClass=true&districtId=${districtId}`
            );
            const data = res.data;
            const newSchoolOptions = (data.schoolOptions ?? []).sort((a, b) =>
                a.name.localeCompare(b.name)
            );
            setSchoolOptions([SelectSchoolOption, ...newSchoolOptions]);
            setClassData(data.classOptions ?? []);
            setClassOptions([SelectClassOption]);
            setFilterState((prev) => {
                const schoolExists = newSchoolOptions.some(
                    (opt) => Number(opt.id) === Number(prev.schoolId)
                );
                return {
                    ...prev,
                    schoolId: schoolExists ? prev.schoolId : Number(SelectSchoolOption.id),
                };
            });
        } catch {
            setError('Failed to fetch schools/classes. Please refresh the page or try again.');
            setSchoolOptions([SelectSchoolOption]);
            setClassOptions([SelectClassOption]);
        }
    };

    const updateClassOptions = (gradeLevelId: number, schoolId: number) => {
        if (
            !gradeLevelId ||
            !schoolId ||
            schoolId === Number(SelectSchoolOption.id) ||
            gradeLevelId === Number(SelectGradeOption.id)
        ) {
            setClassOptions([SelectClassOption]);
            setFilterState((prev) => ({ ...prev, classId: Number(SelectClassOption.id) }));
            return;
        }

        const filtered = classData
            .filter((c) => c.schoolId === schoolId)
            .filter(
                (c) =>
                    !c.gradeLevelMasterIds?.length || c.gradeLevelMasterIds.includes(gradeLevelId)
            )
            .map((c) => ({ id: c.id, name: c.name }));

        if (!filtered.length) {
            setClassOptions([NoClassOption]);
            setFilterState((prev) => ({ ...prev, classId: Number(NoClassOption.id) }));
        } else {
            setClassOptions([SelectClassOption, ...filtered]);
            setFilterState((prev) => {
                const classExists = filtered.some((opt) => Number(opt.id) === Number(prev.classId));
                return {
                    ...prev,
                    classId: classExists ? prev.classId : Number(SelectClassOption.id),
                };
            });
        }
    };

    // --- Exam Pipeline ---
    const maybeFetchExamOptions = async (gradeLevelMasterId: number) => {
        if (!gradeLevelMasterId || gradeLevelMasterId === Number(SelectGradeOption.id)) {
            setAllExamOptions(null);
            setExamLoading(false);
            return;
        }

        setExamLoading(true);
        try {
            const res = await apiRequest<{ examOptions: ExamOption[] }>(
                'GET',
                `utility/filterData?getExam=true&gradeLevelMasterIds=${gradeLevelMasterId}&showPublishedExamsOnly=true`
            );

            const data = (res.data.examOptions ?? []).filter((exam) => exam.schoolIds?.length);
            const sorted = data.sort((a, b) => a.name.localeCompare(b.name));
            setAllExamOptions(sorted);
        } catch {
            setAllExamOptions([]);
            setError('Failed to load exam options. Please refresh the page or try again.');
        } finally {
            setExamLoading(false);
        }
    };

    const filterExamsLocally = (
        classId: number,
        examTypeMasterId: number,
        allExamsData: ExamOption[] | null
    ) => {
        if (!allExamsData) return;

        if (allExamsData.length === 0) {
            setExamOptions([NoExamOption]);
            setFilterState((prev) => ({ ...prev, examId: Number(NoExamOption.id) }));
            return;
        }

        let filtered = allExamsData;

        if (classId && classId !== Number(SelectClassOption.id)) {
            filtered = filtered.filter((e) => !e.classIds?.length || e.classIds.includes(classId));
        }

        if (examTypeMasterId && examTypeMasterId !== Number(AllOption.id)) {
            filtered = filtered.filter((e) => e.examTypeMasterId === examTypeMasterId);
        }

        if (!filtered.length) {
            setExamOptions([NoExamOption]);
            setFilterState((prev) => ({ ...prev, examId: Number(NoExamOption.id) }));
        } else {
            setExamOptions([SelectExamOption, ...filtered]);
            setFilterState((prev) => {
                const examExists = filtered.some((e) => Number(e.id) === Number(prev.examId));
                return {
                    ...prev,
                    examId: examExists ? prev.examId : Number(SelectExamOption.id),
                };
            });
        }
    };

    useEffect(() => {
        maybeFetchExamOptions(filterState.gradeLevelMasterId);
    }, [filterState.gradeLevelMasterId]);

    useEffect(() => {
        filterExamsLocally(filterState.classId, filterState.examTypeMasterId, allExamOptions);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allExamOptions, filterState.classId, filterState.examTypeMasterId]);

    const fetchStudentOptions = async (classId: number) => {
        if (!classId || classId === Number(SelectClassOption.id)) {
            setStudentOptions([AllStudentsOption]);
            setFilterState((prev) => ({ ...prev, studentId: Number(AllStudentsOption.id) }));
            return;
        }
        try {
            const res = await apiRequest<{ studentOptions: Option[] }>(
                'GET',
                `utility/filterData?getStudentsByClass=true&classId=${classId}`
            );
            const data = res.data.studentOptions ?? [];
            setStudentOptions([AllStudentsOption, ...data]);
            setFilterState((prev) => ({ ...prev, studentId: Number(AllStudentsOption.id) }));
        } catch {
            setStudentOptions([AllStudentsOption]);
            setFilterState((prev) => ({ ...prev, studentId: Number(AllStudentsOption.id) }));
        }
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
            <FilterBarContainer>
                <SimpleSingleSelect
                    name="district-select"
                    label="District *"
                    {...getLoadingProps(
                        districtOptions,
                        Number(
                            getValidSelectValue(
                                filterState.districtId,
                                districtOptions,
                                districtOptions[0]?.id
                            )
                        ),
                        filtersLoading
                    )}
                    onChange={(e) => {
                        setFieldsChanged(true);
                        const districtId = Number(e.target.value);
                        setFilterState({
                            ...filterState,
                            districtId,
                            schoolId: Number(SelectSchoolOption.id),
                            gradeLevelMasterId: Number(SelectGradeOption.id),
                            classId: Number(SelectClassOption.id),
                            examId: Number(SelectExamOption.id),
                            studentId: Number(AllStudentsOption.id),
                            examTypeMasterId: Number(AllOption.id),
                        });
                        setSchoolOptions([SelectSchoolOption]);
                        setClassOptions([SelectClassOption]);
                        setExamOptions([SelectExamOption]);
                        setAllExamOptions(null);
                        fetchSchoolClassOptions(districtId);
                    }}
                    sx={{ minWidth: 140, maxWidth: 280 }}
                />
                <SimpleSingleSelect
                    name="school-select"
                    label="School *"
                    {...getLoadingProps(
                        schoolOptions,
                        Number(
                            getValidSelectValue(
                                filterState.schoolId,
                                schoolOptions,
                                schoolOptions[0]?.id
                            )
                        ),
                        filtersLoading
                    )}
                    onChange={(e) => {
                        setFieldsChanged(true);
                        const schoolId = Number(e.target.value);
                        setFilterState({
                            ...filterState,
                            schoolId,
                            gradeLevelMasterId: Number(SelectGradeOption.id),
                            classId: Number(SelectClassOption.id),
                            examId: Number(SelectExamOption.id),
                            studentId: Number(AllStudentsOption.id),
                        });
                        setAllExamOptions(null);
                        updateClassOptions(Number(SelectGradeOption.id), schoolId);
                    }}
                    sx={{ minWidth: 220, maxWidth: 280 }}
                    disabled={
                        filtersLoading || filterState.districtId === Number(SelectDistrictOption.id)
                    }
                />
                <SimpleSingleSelect
                    name="grade-select"
                    label="Grade Level *"
                    {...getLoadingProps(
                        gradeLevelOptions,
                        Number(
                            getValidSelectValue(
                                filterState.gradeLevelMasterId,
                                gradeLevelOptions,
                                gradeLevelOptions[0]?.id
                            )
                        ),
                        filtersLoading
                    )}
                    onChange={(e) => {
                        setFieldsChanged(true);
                        const gradeId = Number(e.target.value);
                        setFilterState({
                            ...filterState,
                            gradeLevelMasterId: gradeId,
                            classId: Number(SelectClassOption.id),
                            examId: Number(SelectExamOption.id),
                        });
                        updateClassOptions(gradeId, filterState.schoolId);
                    }}
                    sx={{ minWidth: 140, maxWidth: 280 }}
                    disabled={
                        filtersLoading || filterState.schoolId === Number(SelectSchoolOption.id)
                    }
                />
                <SimpleSingleSelect
                    name="class-select"
                    label="Class *"
                    {...getLoadingProps(
                        classOptions,
                        Number(
                            getValidSelectValue(
                                filterState.classId,
                                classOptions,
                                classOptions[0]?.id
                            )
                        ),
                        filtersLoading
                    )}
                    onChange={(e) => {
                        setFieldsChanged(true);
                        const classId = Number(e.target.value);
                        setFilterState({
                            ...filterState,
                            classId,
                            studentId: Number(AllStudentsOption.id),
                            examId: Number(SelectExamOption.id),
                        });
                        fetchStudentOptions(classId);
                    }}
                    sx={{ minWidth: 220, maxWidth: 280 }}
                    disabled={
                        filtersLoading ||
                        filterState.schoolId === Number(SelectSchoolOption.id) ||
                        filterState.gradeLevelMasterId === Number(SelectGradeOption.id)
                    }
                />
            </FilterBarContainer>
            <FilterBarContainer>
                <SimpleSingleSelect
                    name="exam-type-select"
                    label="Type"
                    value={filterState.examTypeMasterId}
                    onChange={(e) => {
                        setFieldsChanged(true);
                        const examTypeId = Number(e.target.value);
                        setFilterState({
                            ...filterState,
                            examTypeMasterId: examTypeId,
                            examId: Number(SelectExamOption.id),
                        });
                    }}
                    options={examTypeOptions}
                    sx={{ minWidth: 140, maxWidth: 280 }}
                    disabled={filtersLoading}
                />
                <SimpleSingleSelect
                    name="exam-select"
                    label="Assessment *"
                    {...getLoadingProps(
                        examOptions,
                        Number(
                            getValidSelectValue(filterState.examId, examOptions, examOptions[0]?.id)
                        ),
                        examLoading || filtersLoading
                    )}
                    onChange={(e) => {
                        setFieldsChanged(true);
                        const value = Number(e.target.value);
                        setFilterState({ ...filterState, examId: value });
                        onExamNameChange?.(
                            examOptions.find((o) => Number(o.id) === value)?.name ?? ''
                        );
                    }}
                    sx={{ minWidth: 220, maxWidth: 280 }}
                    disabled={
                        examLoading ||
                        filtersLoading ||
                        filterState.classId === Number(SelectClassOption.id)
                    }
                />
                <SimpleSingleSelect
                    name="student-select"
                    label="Student"
                    value={filterState.studentId}
                    onChange={(e) => {
                        setFieldsChanged(true);
                        setFilterState({ ...filterState, studentId: Number(e.target.value) });
                    }}
                    options={studentOptions}
                    sx={{ minWidth: 220, maxWidth: 280 }}
                    disabled={filtersLoading}
                />
                <SimpleButton
                    onClick={() => {
                        const missing = getMissingFields(filterState);
                        if (missing.length) {
                            setError(
                                `Please select the following required fields before filtering: ${missing.join(', ')}.`
                            );
                            return;
                        }
                        setError('');
                        setFieldsChanged(false);
                        refreshTableData(filterState);
                    }}
                    variant={fieldsChanged ? 'outlined' : 'contained'}
                    color="primary"
                    sx={{ padding: '8px 25px' }}
                >
                    <FilterIcon
                        color={fieldsChanged ? ICON_COLORS.primary : ICON_COLORS.secondary}
                        style={{ width: '16px', height: '16px', marginRight: '8px' }}
                    />{' '}
                    Filter
                </SimpleButton>
            </FilterBarContainer>

            <ErrorMessage error={error} />
        </Box>
    );
};

export default StandardsReportFilters;
